import React, { useMemo } from 'react'
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts'

const SetupAnalysis = ({ trades }) => {
  const setupData = useMemo(() => {
    const setupStats = {}
    
    trades.forEach(trade => {
      const setup = trade.setup_type || 'Unknown'
      if (!setupStats[setup]) {
        setupStats[setup] = {
          name: setup,
          trades: 0,
          wins: 0,
          totalPnL: 0,
          color: getSetupColor(setup)
        }
      }
      
      setupStats[setup].trades += 1
      if ((trade.pnl || 0) > 0) {
        setupStats[setup].wins += 1
      }
      setupStats[setup].totalPnL += trade.pnl || 0
    })

    return Object.values(setupStats).map(setup => ({
      ...setup,
      winRate: setup.trades > 0 ? (setup.wins / setup.trades) * 100 : 0,
      avgPnL: setup.trades > 0 ? setup.totalPnL / setup.trades : 0
    }))
  }, [trades])

  function getSetupColor(setup) {
    const colors = {
      'breakout': '#3b82f6',
      'pullback': '#10b981',
      'reversal': '#f59e0b',
      'trend_continuation': '#8b5cf6',
      'scalp': '#ef4444',
      'swing': '#06b6d4',
      'Unknown': '#6b7280'
    }
    return colors[setup] || colors['Unknown']
  }

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-medium text-gray-900">{data.name}</p>
          <p className="text-sm text-gray-600">Trades: {data.trades}</p>
          <p className="text-sm text-gray-600">Win Rate: {data.winRate.toFixed(1)}%</p>
          <p className="text-sm text-gray-600">Avg P&L: ${data.avgPnL.toFixed(2)}</p>
        </div>
      )
    }
    return null
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-6">Setup Analysis</h3>
      
      {setupData.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <p>No setup data available</p>
        </div>
      ) : (
        <>
          {/* Pie Chart */}
          <div className="h-64 mb-6">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={setupData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={2}
                  dataKey="trades"
                >
                  {setupData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Setup Statistics */}
          <div className="space-y-3">
            {setupData.map((setup, index) => (
              <div key={setup.name} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div 
                    className="w-4 h-4 rounded-full"
                    style={{ backgroundColor: setup.color }}
                  />
                  <span className="font-medium text-gray-900">{setup.name}</span>
                </div>
                <div className="flex items-center space-x-4 text-sm">
                  <span className="text-gray-600">{setup.trades} trades</span>
                  <span className={`font-medium ${
                    setup.winRate >= 50 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {setup.winRate.toFixed(1)}%
                  </span>
                  <span className={`font-medium ${
                    setup.avgPnL >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    ${setup.avgPnL.toFixed(2)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

export default SetupAnalysis